import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Group, Loader, Text } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import CalloutList from '@/shared/surfaces/CalloutList';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import { fetchTopicInsights } from '@/shared/surfaces/TopicInsightsClient';
import type { TopicInsightsRequest } from '@/shared/assist/TopicInsightsContract';

/** One department's share of total AI spend, pre-aggregated for the insights request. */
export interface DepartmentCostInsightRow {
    label: string;
    costUsd: number;
    share: number;
}

interface TokenUsageCostByDepartmentInsightsProps {
    rows: DepartmentCostInsightRow[];
    totalCostUsd: number;
    scopeKey: string;
}

function buildRequest(rows: DepartmentCostInsightRow[], totalCostUsd: number): TopicInsightsRequest {
    return {
        surfaceTitle: 'Cost',
        topicTitle: 'Cost by department',
        question: 'Which departments dominate AI spend, and what stands out about how cost is distributed across teams?',
        lensLabel: 'department',
        metricLabel: 'Spend',
        totals: { spend: totalCostUsd },
        rows: rows
            .map((row) => ({ label: row.label, value: row.costUsd, share: row.share }))
            .sort((a, b) => b.value - a.value),
    };
}

/**
 * The Cost-by-Department screen's AI insights card — the mirror of the
 * by-provider {@link SpendCostInsights}. Sends a small pre-aggregated picture of
 * department spend to the BFF's LLM insights route and renders the returned
 * bullets; a failed or empty reply renders nothing.
 */
export default function TokenUsageCostByDepartmentInsights({ rows, totalCostUsd, scopeKey }: TokenUsageCostByDepartmentInsightsProps): ReactElement | null {
    const [insights, setInsights] = useState<TopicCallout[] | null>(null);
    const [generating, setGenerating] = useState(false);
    const request = useMemo(() => buildRequest(rows, totalCostUsd), [rows, totalCostUsd]);

    useEffect((): (() => void) => {
        let cancelled = false;
        setInsights(null);
        if (request.rows.length === 0) return () => undefined;
        setGenerating(true);
        void fetchTopicInsights(request)
            .then((reply) => {
                if (!cancelled && reply.insights.length > 0) setInsights(reply.insights);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setGenerating(false);
            });
        return (): void => {
            cancelled = true;
        };
    }, [request, scopeKey]);

    if ((insights === null || insights.length === 0) && !generating) return null;

    return (
        <SurfaceCard $padding="md">
            <Group gap={6} mb="sm" wrap="nowrap" justify="space-between">
                <Group gap={6} wrap="nowrap">
                    <Sparkles size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                        Insights
                    </Text>
                </Group>
                {generating && (
                    <Group gap={6} wrap="nowrap">
                        <Loader size={10} />
                        <Text size="xs" c="dimmed">
                            generating…
                        </Text>
                    </Group>
                )}
            </Group>
            {insights !== null && <CalloutList callouts={insights} accent="teal" />}
        </SurfaceCard>
    );
}
